import { useGetContext, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedName } from '@iwIntlProvider/FormattedName';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconDownload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './SignDocumentAction.messages';
import styles from './SignDocumentAction.module.css';
import { TSignDocument } from './SignDocumentsHelper';
import { FUNDING_STEP_ACTION } from '../../Funding.actions';
import StepItem from '../stepItem/StepItem';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    handleFundingStepAction: (step: TSignDocument) => {
        let stepType = 'SIGN_DOCUMENT';
        if (step.signatureMethod === 'WET') {
            stepType = 'SIGN_DOCUMENT_WET';
        }
        dispatch({
            type: FUNDING_STEP_ACTION,
            step: stepType,
            signDocumentAction: step
        });
    },
    downloadSignDocument: (step: TSignDocument) => {
        dispatch({
            type: FUNDING_STEP_ACTION,
            step: 'VIEW_SIGN_DOCUMENT',
            signDocumentAction: step
        });
    }
});

const SignDocumentAction = ({
    step,
    handleFundingStepAction,
    downloadSignDocument
}: {
    step: TSignDocument;
} & ReturnType<typeof mapDispatchToProps>) => {
    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';
    const documentType = step.type.toLowerCase();
    const nonApplicantPgConsentSplitTest = useSplitTests(
        'non_applicant_pg_consent'
    );

    const formattedPersonToSign = (
        <FormattedName
            possessive={true}
            firstName={step.personToSign?.first_name}
            lastName={step.personToSign?.last_name}
        />
    );

    const formattedPersonForCaption = (
        <FormattedName
            firstName={step.personToSign?.first_name}
            lastName={step.personToSign?.last_name}
        />
    );

    const requiresNonApplicantSigning =
        !isAccessUser && step.requiresNonApplicantSigning;

    const title =
        step.type === 'guarantee' ? (
            <FormattedMessage
                {...messages[`${documentType}_label` as keyof typeof messages]}
                values={{
                    requiresNonApplicantSigning,
                    formattedPersonToSign
                }}
            />
        ) : (
            <FormattedMessage
                {...messages[`${documentType}_label` as keyof typeof messages]}
            />
        );

    const caption =
        step.status === 'REQUIRED' ? (
            step.signatureMethod === 'WET' ? (
                <>
                    <FormattedMessage
                        {...messages.wetSignDocumentsFooter}
                        values={{
                            documentsLink: (
                                <a
                                    className={styles.documentTypeFooterLink}
                                    href={`mailto:${messages.documentsEmailAddress.defaultMessage}`}
                                >
                                    <FormattedMessage
                                        {...messages.documentsEmailAddress}
                                    />
                                </a>
                            )
                        }}
                    />
                    {/* DE requires post address to be shown */}
                    {getCountry() === 'DE' && (
                        <div className={styles.postalAddress}>
                            <FormattedMessage
                                {...messages.companyName}
                                tagName="div"
                            />
                            <FormattedMessage
                                {...messages.addressLineOne}
                                tagName="div"
                            />
                            <FormattedMessage
                                {...messages.addressLineTwo}
                                tagName="div"
                            />
                            <FormattedMessage
                                {...messages.addressLineThree}
                                tagName="div"
                            />
                        </div>
                    )}
                </>
            ) : (
                <FormattedMessage
                    {...messages.caption}
                    values={{
                        requiresNonApplicantSigning,
                        formattedPersonToSign
                    }}
                />
            )
        ) : requiresNonApplicantSigning ? (
            <FormattedMessage
                {...messages.access_user_caption_completed}
                values={{
                    formattedPersonForCaption
                }}
            />
        ) : (
            <FormattedMessage {...messages.caption_completed} />
        );

    return (
        <StepItem
            title={title}
            caption={caption}
            action={
                step.status === 'COMPLETED' ? (
                    <Button
                        variant="tertiary"
                        onClick={() => downloadSignDocument(step)}
                        aria-label="download document"
                    >
                        <IconDownload
                            height="24"
                            width="24"
                            fill="rgba(16, 47, 86, 1)"
                            xlinkTitle="download"
                        />
                    </Button>
                ) : nonApplicantPgConsentSplitTest &&
                  requiresNonApplicantSigning ? null : (
                    <Button
                        variant="primary"
                        onClick={() => {
                            tracking.ampli.fundingStepCtaClicked({
                                funding_step: 'Sign document'
                            });
                            handleFundingStepAction(step);
                        }}
                    >
                        {step.signatureMethod === 'WET' ? (
                            <FormattedMessage {...messages.action_wet} />
                        ) : (
                            <FormattedMessage {...messages.action} />
                        )}
                    </Button>
                )
            }
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(SignDocumentAction);
